import React, { Component } from "react";
import { AxiosResponse } from "axios";
import { Link } from "react-router-dom";
import { getRequest } from "../../utils/httpRequest";
import { errorNotification, pluralizeCount } from "../../utils/helpers";
import SignupBox from "../RoryQuotes/SignupBox";
import Sources from "../RoryQuotes/Sources";

const RORY = "rory-sutherland";

// A thought leader's page: who they are, what they teach here, what we have
// written about them. Every part is built from fields the admin already has,
// so it works for all of them; the pull quote, the highlight and the "At a
// glance" card are optional and simply do not appear when blank.

interface SkillArea {
  id: number;
  name: string;
  slug: string;
  course_filter_path: string;
  skill_area_path: string;
}

interface Course {
  id: number;
  name: string;
  slug: string;
  course_path: string;
  tile_description?: string;
  preview_mini_description?: string;
  lessons_count?: number;
  skill_area_name?: string;
  sample?: boolean;
  preview_headshot_image_url?: string;
  preview_headshot_alt_image_url?: string;
}

interface GlanceItem {
  label: string | null;
  value: string;
}

interface RelatedPost {
  id: number;
  title: string;
  url: string;
  excerpt?: string;
  hero_image_url?: string;
  published_at?: string;
}

interface ThoughtLeader {
  id: number;
  name: string;
  slug: string;
  role_descriptor: string;
  job_title: string;
  primary_skill_area: string;
  description: string;
  formatted_bio: string;
  pull_quote?: string;
  bio_highlight?: string;
  at_a_glance: GlanceItem[];
  website_url?: string;
  twitter_url?: string;
  linkedin_url?: string;
  lessons_total: number;
  image_url: string;
  image_alt: string;
  profile_path: string;
  related_posts: RelatedPost[];
}

interface Props {
  match: {
    params: {
      slug: string;
    };
  };
}

interface State {
  loading: boolean;
  thoughtLeader: ThoughtLeader;
  skillAreas: SkillArea[];
  courses: Course[];
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "1 Jul 2024", not the browser's "Mon Jul 01 2024".
const formatDate = (iso?: string) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

// The person's first name, for headings like "Learn from Rory". Falls back to
// the full name for single-word names and for organisations.
const firstName = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 && parts.length < 4 ? parts[0] : name;
};

// The bio arrives as HTML paragraphs from Markdown. The first becomes the
// lede; the highlight card, when there is one, sits after the second.
const splitParagraphs = (html: string): string[] => {
  const matches = html.match(/<p\b[^>]*>[\s\S]*?<\/p>/gi);
  return matches && matches.length > 0 ? matches : [html];
};

const WebsiteIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M3 12h18M12 3c3 3.2 3 14.8 0 18M12 3c-3 3.2-3 14.8 0 18" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.5 3h3.1l-6.8 7.8L21.8 21h-6.3l-4.9-6.4L5 21H1.9l7.3-8.3L1.5 3h6.4l4.4 5.9L17.5 3zm-1.1 16.2h1.7L6.6 4.7H4.8l11.6 14.5z" fill="currentColor" />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6.9 8.5H3.4V21h3.5V8.5zM5.2 3a2 2 0 100 4.1 2 2 0 000-4.1zM21 13.4c0-3.4-1.8-5.2-4.3-5.2-1.9 0-2.9 1-3.4 1.8V8.5H9.8V21h3.5v-6.6c0-1.7.6-2.9 2.2-2.9 1.5 0 2 1.2 2 2.9V21H21v-7.6z" fill="currentColor" />
  </svg>
);

class ThoughtLeaderProfile extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      thoughtLeader: null,
      skillAreas: [],
      courses: []
    };
  }

  componentDidMount() {
    this.fetchThoughtLeader();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.match.params.slug !== this.props.match.params.slug) {
      this.fetchThoughtLeader();
    }
  }

  fetchThoughtLeader = () => {
    this.setState({
      loading: true,
      thoughtLeader: null,
      skillAreas: [],
      courses: []
    });

    return getRequest(`/api/thought-leaders/${this.props.match.params.slug}.json`)
      .then((response: AxiosResponse) => {
        this.setState({
          thoughtLeader: response.data.thought_leader,
          skillAreas: response.data.skill_areas,
          courses: response.data.courses,
          loading: false
        });
      })
      .catch(error => {
        this.setState({ loading: false });
        errorNotification(
          "Error loading thought leader",
          JSON.stringify(error.response?.data || error.message)
        );
      });
  };

  renderSocialLinks() {
    const { thoughtLeader } = this.state;
    const links = [
      { url: thoughtLeader.website_url, label: "Website", icon: <WebsiteIcon /> },
      { url: thoughtLeader.twitter_url, label: "X", icon: <XIcon /> },
      { url: thoughtLeader.linkedin_url, label: "LinkedIn", icon: <LinkedInIcon /> }
    ].filter(link => link.url);

    if (links.length === 0) return null;

    return (
      <div className="tlp-social" aria-label={`${thoughtLeader.name} elsewhere`}>
        {links.map(link => (
          <a key={link.label} href={link.url} target="_blank" rel="noopener noreferrer" title={link.label} aria-label={link.label}>
            {link.icon}
          </a>
        ))}
      </div>
    );
  }

  renderHero() {
    const { thoughtLeader, skillAreas, courses } = this.state;
    const posts = thoughtLeader.related_posts.length;
    const showLessons = thoughtLeader.lessons_total > 0;

    return (
      <header className="tlp-hero">
        <div className="tlp-hero__grid">
          <div className="tlp-hero__copy">
            <span className="tlp-kicker">Thought leader</span>
            <h1>{thoughtLeader.name}</h1>
            {(thoughtLeader.role_descriptor || thoughtLeader.job_title) && (
              <p className="tlp-hero__role">{thoughtLeader.role_descriptor || thoughtLeader.job_title}</p>
            )}
            {skillAreas.length > 0 && (
              <div className="tlp-tags" aria-label="Skill areas">
                {skillAreas.map(skillArea => (
                  <Link to={skillArea.skill_area_path} key={skillArea.id}>
                    {skillArea.name}
                  </Link>
                ))}
              </div>
            )}
            {thoughtLeader.pull_quote && (
              <blockquote className="tlp-quote">
                <p>“{thoughtLeader.pull_quote}”</p>
                <cite>{thoughtLeader.name}</cite>
              </blockquote>
            )}
            <div className="tlp-hero__actions">
              {courses.length > 0 && (
                <a className="tlp-btn" href="#courses-by-thought-leader" data-smooth-scroll-to="#courses-by-thought-leader">
                  Explore {firstName(thoughtLeader.name)}&apos;s courses
                </a>
              )}
              {thoughtLeader.slug === RORY && (
                <Link className="tlp-btn" to="/rory-quotes">Try the Rory Quote Finder</Link>
              )}
              {this.renderSocialLinks()}
            </div>
          </div>

          <div className="tlp-hero__visual">
            {thoughtLeader.image_url && (
              <div className="tlp-portrait">
                <img src={thoughtLeader.image_url} alt={thoughtLeader.image_alt} />
              </div>
            )}
            <div className="tlp-stat tlp-stat--courses">
              <strong>{courses.length}</strong>
              <span>{pluralizeCount(courses.length, "Course", "Courses")}</span>
            </div>
            {posts > 0 && (
              <div className="tlp-stat tlp-stat--posts">
                <strong>{posts}</strong>
                <span>{pluralizeCount(posts, "Post", "Posts")}</span>
              </div>
            )}
            {showLessons && (
              <div className="tlp-stat tlp-stat--lessons">
                <strong>{thoughtLeader.lessons_total}</strong>
                <span>Lessons</span>
              </div>
            )}
          </div>
        </div>
      </header>
    );
  }

  renderAbout() {
    const { thoughtLeader } = this.state;
    const paragraphs = splitParagraphs(thoughtLeader.formatted_bio || "");
    const [lede, ...rest] = paragraphs;
    const glance = thoughtLeader.at_a_glance || [];
    // The highlight goes after the second paragraph, or at the end of a short bio.
    const highlightAfter = Math.min(1, Math.max(rest.length - 1, 0));

    if (!lede) return null;

    return (
      <section className={`tlp-section tlp-about${glance.length > 0 ? " has-glance" : ""}`}>
        <div className="tlp-about__grid">
          {glance.length > 0 && (
            <aside className="tlp-glance">
              <h3>At a glance</h3>
              <dl>
                {glance.map((item, index) => (
                  <div key={index}>
                    {item.label && <dt>{item.label}</dt>}
                    <dd>{item.value}</dd>
                  </div>
                ))}
              </dl>
            </aside>
          )}
          <div className="tlp-bio">
            <h2>About {firstName(thoughtLeader.name)}</h2>
            <div className="tlp-bio__lede" dangerouslySetInnerHTML={{ __html: lede }} />
            {rest.map((paragraph, index) => (
              <React.Fragment key={index}>
                <div className="tlp-bio__body" dangerouslySetInnerHTML={{ __html: paragraph }} />
                {thoughtLeader.bio_highlight && index === highlightAfter && (
                  <div className="tlp-highlight">
                    <span>In {firstName(thoughtLeader.name)}&apos;s words</span>
                    {thoughtLeader.bio_highlight}
                  </div>
                )}
              </React.Fragment>
            ))}
            {thoughtLeader.bio_highlight && rest.length === 0 && (
              <div className="tlp-highlight">
                <span>In {firstName(thoughtLeader.name)}&apos;s words</span>
                {thoughtLeader.bio_highlight}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  renderCourseImage(course: Course) {
    const src = course.preview_headshot_image_url || course.preview_headshot_alt_image_url;
    return src ? <img src={src} alt="" loading="lazy" /> : null;
  }

  renderCourseMeta(course: Course) {
    const parts = [];
    if (course.lessons_count) parts.push(`${course.lessons_count} lessons`);
    if (course.skill_area_name) parts.push(course.skill_area_name);
    return parts.join(" · ");
  }

  renderCourses() {
    const { thoughtLeader, courses } = this.state;
    if (courses.length === 0) return null;

    // Every course they teach or appear in, in the site's own order, so the
    // first is the one we would point a newcomer at.
    const list = courses;
    const [featured, ...others] = list;
    const blurb = featured.tile_description || featured.preview_mini_description;
    const name = firstName(thoughtLeader.name);

    return (
      <section className="tlp-section tlp-courses" id="courses-by-thought-leader">
        <div className="tlp-section__head">
          <h2>Learn from {name}</h2>
          <p>
            {list.length === 1
              ? `The course ${name} teaches on 42courses.`
              : `${list.length} courses ${name} teaches or appears in. If you only take one, take the first.`}
          </p>
        </div>

        <Link className="tlp-feature" to={featured.course_path}>
          <div className="tlp-feature__img">{this.renderCourseImage(featured)}</div>
          <div className="tlp-feature__body">
            <span className="tlp-kicker">Start here</span>
            <h3>{featured.name}</h3>
            {blurb && <p>{blurb}</p>}
            <div className="tlp-meta">
              {featured.lessons_count > 0 && <span>{featured.lessons_count} lessons</span>}
              <span>CPD certified</span>
              {featured.skill_area_name && <span>{featured.skill_area_name}</span>}
            </div>
            <span className="tlp-btn">Explore this course</span>
          </div>
        </Link>

        {others.length > 0 && (
          <div className="tlp-course-grid">
            {others.map(course => (
              <Link className="tlp-course" key={course.id} to={course.course_path}>
                <div className="tlp-course__img">{this.renderCourseImage(course)}</div>
                <div className="tlp-course__body">
                  <h4>{course.name}</h4>
                  {this.renderCourseMeta(course) && <span>{this.renderCourseMeta(course)}</span>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    );
  }

  renderPost(post: RelatedPost, featured: boolean) {
    return (
      <a className={`tlp-post${featured ? " tlp-post--featured" : ""}`} href={post.url} key={post.id}>
        {post.hero_image_url ? (
          <div className="tlp-post__img">
            <img src={post.hero_image_url} alt="" loading="lazy" />
          </div>
        ) : (
          <div className="tlp-post__img tlp-post__img--blank" aria-hidden="true">
            <span>42</span>
          </div>
        )}
        <div className="tlp-post__body">
          {post.published_at && <em>{formatDate(post.published_at)}</em>}
          <strong>{post.title}</strong>
          {featured && post.excerpt && <p>{post.excerpt}</p>}
        </div>
      </a>
    );
  }

  renderPosts() {
    const { thoughtLeader } = this.state;
    const posts = thoughtLeader.related_posts;
    if (posts.length === 0) return null;
    const [latest, ...rest] = posts;
    const name = firstName(thoughtLeader.name);

    return (
      <section className="tlp-section tlp-posts">
        <div className="tlp-section__head">
          <h2>{name} on the blog</h2>
          <p>{posts.length} {posts.length === 1 ? "post" : "posts"} about {thoughtLeader.name} and the ideas {name} teaches.</p>
        </div>
        <div className="tlp-post-grid">
          {this.renderPost(latest, rest.length > 0)}
          {rest.map(post => this.renderPost(post, false))}
        </div>
      </section>
    );
  }

  // Rory has a quote finder of his own on the site; nobody else does yet.
  renderQuotes() {
    if (this.state.thoughtLeader.slug !== RORY) return null;

    return (
      <section className="tlp-section tlp-quotes">
        <Link className="tlp-quotes__link" to="/rory-quotes">
          <img src="/rory-quotes/rory-sketch.png" alt="" width="96" height="96" />
          <span>
            <strong>Rory Sutherland quotes</strong>
            Over 200 verified lines, searchable by topic, ready to share. Or a quote and a behavioural bias by email every Monday.
          </span>
        </Link>
      </section>
    );
  }

  renderSources() {
    if (this.state.thoughtLeader.slug !== RORY) return null;
    return (
      <>
        <div className="rq rq--embed">
          <SignupBox />
        </div>
        <Sources title="The book and the talks." />
      </>
    );
  }

  renderClosing() {
    const { thoughtLeader, courses } = this.state;
    const featured = courses[0];
    if (!featured) return null;

    return (
      <section className="tlp-band">
        <div className="tlp-band__inner">
          <div>
            <h2>Start with {featured.name}</h2>
            <p>
              {firstName(thoughtLeader.name)}&apos;s course
              {featured.lessons_count ? `, ${featured.lessons_count} lessons` : ""}, CPD certified and marked by humans.
              Or get every course, this one included, with the All Courses Pass.
            </p>
          </div>
          <div className="tlp-band__actions">
            <Link className="tlp-btn tlp-btn--sunshine" to={featured.course_path}>Enrol now</Link>
            <Link className="tlp-btn tlp-btn--ghost" to="/pricing">See the All Courses Pass</Link>
          </div>
        </div>
      </section>
    );
  }

  render() {
    const { loading, thoughtLeader } = this.state;

    if (loading) {
      return (
        <main className="thought-leader-profile">
          <div className="ui active inverted dimmer">
            <div className="ui text loader">Loading</div>
          </div>
        </main>
      );
    }

    if (!thoughtLeader) {
      return (
        <main className="thought-leader-profile">
          <section className="thought-leader-profile__empty">
            <h2>Thought leader not found</h2>
            <p>We could not load this profile.</p>
          </section>
        </main>
      );
    }

    return (
      <div className="thought-leader-profile">
        {this.renderHero()}
        <main className="tlp-main">
          {this.renderAbout()}
          {this.renderQuotes()}
          {this.renderCourses()}
          {this.renderSources()}
          {this.renderPosts()}
          {this.renderClosing()}
        </main>
      </div>
    );
  }
}

export default ThoughtLeaderProfile;
